<template>
  <div class="card">
    <header class="card__header" ref="cardHeader">
      <h2 class="card__title text--ellipsis">
        <span class="card__title--light">Top 5 </span>
        <span v-if="type === 'costCenters'" :title="`Top 5 ${$t('Common.costCenters')}`">
          {{ $t('Common.costCenters') }}
        </span>
        <span class="text--capitalize" v-else :title="`Top 5 ${$t('Common.employees')}`">
          {{ $t('Common.employees') }}
        </span>
      </h2>

      <router-link
        v-if="status === 'success'"
        :to="route"
        :title="$t('Common.seeMore')"
        class="card__action"
      >
        <span>{{ $t('Common.seeMore') }}</span>
        <v-icon>add</v-icon>
      </router-link>
    </header>

    <div v-if="status === 'success'">
      <div
        v-if="items.length"
        class="card__body"
        :style="`height: ${cardBodyHeight}px;`"
      >
        <v-layout
          wrap
          justify-center
          row
          :fill-height="items.length > 4"
        >
          <v-flex
            v-for="(item, index) in items"
            :key="index"
            class="list__item"
            xs12
          >
            <span class="list__item--wrapper">
              <span
                class="list__item--title text--ellipsis"
                @click="handleClick(item)"
              >
                {{ item.name }}
              </span>
              <span class="list__item--value">{{ item.value }} ({{ `${item.percent.toFixed(2)}%` }})</span>
            </span>
            <span class="list__item--progress">
              <span class="list__item--bar" :style="`width: ${item.percent}%`"></span>
            </span>
          </v-flex>
        </v-layout>
      </div>

      <v-layout
        v-else
        class="card__content text-xs-center"
        :style="`height: ${cardBodyHeight}px;`"
        align-center
        justify-center
        row
        :fill-height="items.length > 4"
      >
        <div v-if="type === 'costCenters'" class="card--message">
          <p><strong v-html="$t('Views.HomePage.top5costCenterEmpty')" /></p>
          <p>
            <span v-html="$t('Views.HomePage.top5costCenterHelp')" />&nbsp;<a href="https://99empresas.zendesk.com/hc/pt-br" :title="$t('Common.clickHere')" target="_blank" class="text--link accent--text">{{ $t('Common.clickHere') }}</a>&nbsp;<span v-html="$t('Views.HomePage.top5andLearnMore')" />
          </p>
        </div>

        <div v-if="type === 'employees'" class="card--message">
          <p><strong v-html="$t('Views.HomePage.top5employeerEmpty')" /></p>
          <p>
            <span v-html="$t('Views.HomePage.top5employeerHelp')" /><br class="hidden-sm-and-down"><a href="https://99empresas.zendesk.com/hc/pt-br" :title="$t('Common.clickHere')" target="_blank" class="text--link accent--text">{{ $t('Common.clickHere') }}</a>&nbsp;<span v-html="$t('Views.HomePage.top5andSeeHow')" />
          </p>
        </div>
      </v-layout>
    </div>

    <v-layout
      v-if="status === 'error'"
      class="card__content card--message text-xs-center"
      :style="`height: ${cardBodyHeight}px;`"
      align-center
      justify-center
      row
      fill-height
    >
      <span>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          href="#"
          :title="$t('Common.tryAgain')"
          class="text--link accent--text"
          @click.prevent="tryAgain"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </span>
    </v-layout>

    <v-layout
      v-if="status === 'loading'"
      class="card__content text-xs-center"
      :style="`height: ${cardBodyHeight}px;`"
      align-center
      justify-center
      row
      fill-height
    >
      <v-progress-circular indeterminate color="accent" />
    </v-layout>
  </div>
</template>

<script>
export default {
  name: 'Top5',
  props: {
    type: {
      type: String
    },
    route: {
      type: Object
    },
    items: {
      type: [Array, null]
    },
    status: {
      type: String,
      default: 'loading'
    },
    minHeight: {
      type: Number,
      default: 0
    }
  },
  computed: {
    cardBodyHeight () {
      const cardHeader = document.querySelector('[data-id="cardHeader"]')
      return !cardHeader ? this.minHeight : this.minHeight - cardHeader.offsetHeight
    }
  },
  methods: {
    tryAgain () {
      this.$emit('tryAgain')
    },

    handleClick (data) {
      if (this.type === 'costCenters') {
        this.$emit('getCostCenterData', data.info)
      }

      if (this.type === 'employees') {
        this.$emit('getEmployeeData', data.info)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.card {
  background-color: #fff;
  margin: 12px;
  min-height: 293px;
}

.card__title {
  padding-bottom: 12px;
  padding-top: 12px;
  padding-right: 80px;

  span {
    font-weight: 800;
  }

  .card__title--light {
    font-weight: 400;
  }
}

.card__body {
  padding-top: 0;
}

.card__content {
  min-height: 215px;
}

.list {
  font-weight: 700;
  padding-left: 18px;
}

.list__item {
  margin-top: 16px;
}

.list__item--wrapper {

  &:after {
    height: 1px;
    clear: both;
    content: '';
    display: block;
    width: 100%;
  }
}

.list__item--title,
.list__item--value {
  font-weight: normal;
  float: left;
  width: 50%;
}

.list__item--title {
  color: $accent;
  cursor: pointer;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
}

.list__item--value {
  font-family: 'Montserrat', sans-serif;
  font-weight: 600;
  text-align: right;
}

.list__item--progress {
  background-color: #f2f2f2;
  display: block;
  height: 5px;
}

.list__item--bar {
  background-color: #00aeff;
  display: block;
  height: 5px;
}

.card__error {
  font-size: 12px;
  padding: 0 40px;
}
</style>
